import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Stack, alpha, Tab, Tabs, Autocomplete,
  Switch, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Radio, RadioGroup, FormControlLabel, Tooltip, Divider,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import SaveIcon           from '@mui/icons-material/Save';
import AddIcon            from '@mui/icons-material/Add';
import DeleteIcon         from '@mui/icons-material/Delete';
import PlayArrowIcon      from '@mui/icons-material/PlayArrow';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ErrorIcon          from '@mui/icons-material/Error';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import LayersIcon         from '@mui/icons-material/Layers';
import CalculateIcon      from '@mui/icons-material/Calculate';
import InputIcon          from '@mui/icons-material/Input';
import VerifiedIcon       from '@mui/icons-material/Verified';
import TuneIcon           from '@mui/icons-material/Tune';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import {
  createElementType, updateElementType, fetchElementDetail,
  evaluateFormula, fetchElementSetupLookups,
} from '../services/api';
import { useSnackbar } from 'notistack';
import { PATTERNS } from '../config/fieldConfig';

// Identifier pattern for variable names — lower_snake_case, must start with letter/underscore
const VAR_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

// ── Design tokens ──────────────────────────────────────────────────────────────
const TEAL   = '#0891b2';
const TEAL_D = '#0e7490';
const TEAL_L = '#ecfeff';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL },
  },
  '& label.Mui-focused': { color: TEAL },
};

const cardSx = {
  mb: 2.5, p: 2.5, bgcolor: 'white',
  borderRadius: '14px', border: '1.5px solid #E8EDF5',
};

const SectionHeader = ({ icon, label, sub }) => (
  <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
    <Box sx={{
      width: 30, height: 30, borderRadius: '8px',
      bgcolor: alpha(TEAL, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 16, color: TEAL } })}
    </Box>
    <Box>
      <Typography fontSize={12.5} fontWeight={700} color="text.secondary" letterSpacing={0.6} textTransform="uppercase">
        {label}
      </Typography>
      {sub && <Typography fontSize={11} color="text.secondary">{sub}</Typography>}
    </Box>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EDF5' }} />
  </Stack>
);

const FlagRow = ({ label, hint, checked, onChange }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between"
    sx={{ py: 1.2, borderBottom: '1px solid #F1F5F9', '&:last-child': { borderBottom: 0 } }}>
    <Box>
      <Typography fontSize={13} fontWeight={600} color="#374151">{label}</Typography>
      {hint && <Typography fontSize={11} color="text.secondary">{hint}</Typography>}
    </Box>
    <Switch size="small" checked={!!checked} onChange={e => onChange(e.target.checked)}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
      }} />
  </Stack>
);

// ── Helpers ────────────────────────────────────────────────────────────────────
const TABS = [
  { label: 'Basic',       icon: <LayersIcon />,    key: 'basic' },
  { label: 'Calculation', icon: <CalculateIcon />,  key: 'calc' },
  { label: 'Inputs',      icon: <InputIcon />,     key: 'inputs' },
  { label: 'Eligibility', icon: <VerifiedIcon />,  key: 'eligibility' },
  { label: 'Settings',    icon: <TuneIcon />,      key: 'settings' },
];

const OPERATORS = ['=', '<', '>', '<=', '>=', '!='];

const CONDITION_FIELDS = [
  { value: 'BASIC',      label: 'Basic Salary' },
  { value: 'GROSS',      label: 'Gross Salary' },
  { value: 'CTC',        label: 'CTC' },
  { value: 'department', label: 'Department' },
  { value: 'location',   label: 'Location' },
  { value: 'grade',      label: 'Grade' },
];

function slugify(str) {
  return str.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function extractVars(expr) {
  const matches = expr.match(/\b[a-zA-Z_]\w*\b/g) || [];
  return [...new Set(matches)];
}

function autoCode(name) {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return slugify(name);
  return slugify(words.map(w => w[0]).join(''));
}

// ── Empty state factories ──────────────────────────────────────────────────────
const EMPTY_BASIC = { company_id: '', bg_id: '', ldg_id: '', payroll_id: '', element_name: '', element_code: '', type: 'earning', active_flag: 'Y' };
const EMPTY_CALC  = { calculation_rule: 'flat', flat_amount: '', percentage_value: '', base_element: 'BASIC', formula_name: '', formula_code: '', formula_expression: '' };
const EMPTY_SETTINGS = { taxable_flag: false, pf_applicable: false, esi_applicable: false, show_in_payslip: true, pro_rata_flag: false, processing_priority: 99 };
const EMPTY_INPUT = { input_value_name: '', data_type: 'number', default_value: '', required_flag: true };
const EMPTY_RULE  = { condition_field: 'BASIC', operator: '=', condition_value: '' };

// ═══════════════════════════════════════════════════════════════════════════════
export default function ElementSetupPage({ open, onClose, record, onSaved }) {
  const isEdit = !!record;
  const { enqueueSnackbar } = useSnackbar();

  const [tab,         setTab]         = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [lookups,     setLookups]     = useState({ companies: [], businessGroups: [], legislations: [], payrolls: [], departments: [], locations: [], elements: [] });
  const [basic,       setBasic]       = useState(EMPTY_BASIC);
  const [calc,        setCalc]        = useState(EMPTY_CALC);
  const [inputs,      setInputs]      = useState([]);
  const [rules,       setRules]       = useState([]);
  const [settings,    setSettings]    = useState({ ...EMPTY_SETTINGS, gl_account_id: '' });
  const [errors,      setErrors]      = useState({});
  const [testVars,    setTestVars]    = useState({});
  const [testResult,  setTestResult]  = useState(null);
  const [testRunning, setTestRunning] = useState(false);
  // Track whether user has manually edited code fields. Once true, name typing
  // no longer overwrites the code. Stays true even after clearing the name.
  const [elementCodeManual, setElementCodeManual] = useState(false);
  const [formulaCodeManual, setFormulaCodeManual] = useState(false);

  // ── Load lookups ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    fetchElementSetupLookups()
      .then(r => setLookups(r.data.data))
      .catch(() => {});
  }, [open]);

  // ── Reset / load record ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setTab(0);
    setErrors({});
    setTestResult(null);
    setTestVars({});

    if (!record) {
      // Auto-fill from active payroll context if set
      const ctx = (() => {
        try { return JSON.parse(localStorage.getItem('payroll.activeContext') || 'null'); }
        catch { return null; }
      })();
      setBasic({
        ...EMPTY_BASIC,
        company_id: ctx?.company_id || '',
        bg_id:      ctx?.bg_id      || '',
        ldg_id:     ctx?.ldg_id     || '',
      });
      setCalc({ ...EMPTY_CALC });
      setInputs([]);
      setRules([]);
      setSettings({ ...EMPTY_SETTINGS });
      setElementCodeManual(false);
      setFormulaCodeManual(false);
      return;
    }

    setLoading(true);
    const id = record.element_id || record._id;
    fetchElementDetail(id)
      .then(r => {
        const { element: el, formula, inputs: invs = [], eligibility: elg = [] } = r.data.data;
        setBasic({
          company_id:   el.company_id  || '',
          bg_id:        el.bg_id       || '',
          ldg_id:       el.ldg_id      || '',
          payroll_id:   el.payroll_id  || '',
          element_name: el.element_name || '',
          element_code: el.element_code || '',
          type:         el.type        || 'earning',
          active_flag:  el.active_flag || 'Y',
        });
        setCalc({
          calculation_rule:   el.calculation_rule   || 'flat',
          flat_amount:        el.flat_amount         ?? '',
          percentage_value:   el.percentage_value    ?? '',
          base_element:       el.base_element        || 'BASIC',
          formula_name:       formula?.formula_name  || '',
          formula_code:       formula?.formula_code  || '',
          formula_expression: formula?.formula_expression || '',
        });
        setInputs(invs.map(i => ({
          input_value_name: i.input_value_name,
          data_type:        i.data_type    || 'number',
          default_value:    i.default_value ?? '',
          required_flag:    i.required_flag ?? true,
        })));
        setRules(elg.map(r => ({
          condition_field: r.condition_field,
          operator:        r.operator,
          condition_value: r.condition_value,
        })));
        setSettings({
          taxable_flag:       el.taxable_flag    ?? false,
          pf_applicable:      el.pf_applicable   ?? false,
          esi_applicable:     el.esi_applicable  ?? false,
          show_in_payslip:    el.show_in_payslip ?? true,
          pro_rata_flag:      el.pro_rata_flag   ?? false,
          processing_priority: el.processing_priority ?? 99,
          gl_account_id:      el.gl_account_id   || '',
        });
        // Existing records have their codes set already — treat them as manually-edited
        // so name typing doesn't overwrite the stored value.
        setElementCodeManual(!!el.element_code);
        setFormulaCodeManual(!!formula?.formula_code);
      })
      .catch(() => enqueueSnackbar('Failed to load element details', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [open, record]);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const isFormula  = calc.calculation_rule === 'formula';
  const formulaVars = useMemo(() => extractVars(calc.formula_expression), [calc.formula_expression]);
  const declaredNames = useMemo(() => inputs.map(i => i.input_value_name), [inputs]);
  const undeclaredVars = useMemo(() => formulaVars.filter(v => !declaredNames.includes(v)), [formulaVars, declaredNames]);

  // Sync test var keys when formula changes
  useEffect(() => {
    setTestVars(prev => {
      const next = {};
      formulaVars.forEach(v => { next[v] = prev[v] ?? ''; });
      return next;
    });
    setTestResult(null);
  }, [calc.formula_expression]);

  // ── Setters ────────────────────────────────────────────────────────────────────
  const setB = (field, val) => {
    setBasic(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => { const n = { ...p }; delete n[field]; return n; });
  };
  const setC = (field, val) => {
    setCalc(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => { const n = { ...p }; delete n[field]; return n; });
  };

  const addInput = () => setInputs(p => [...p, { ...EMPTY_INPUT }]);
  const removeInput = i => setInputs(p => p.filter((_, idx) => idx !== i));
  const setInput = (i, field, val) => setInputs(p => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const addRule = () => setRules(p => [...p, { ...EMPTY_RULE }]);
  const removeRule = i => setRules(p => p.filter((_, idx) => idx !== i));
  const setRule = (i, field, val) => setRules(p => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const autoFillInput = (varName) => {
    if (declaredNames.includes(varName)) return;
    setInputs(p => [...p, { input_value_name: varName, data_type: 'number', default_value: '', required_flag: true }]);
    setTab(2);
  };

  // ── Formula test ──────────────────────────────────────────────────────────────
  const handleRunTest = async () => {
    if (!calc.formula_expression.trim()) return;
    setTestRunning(true);
    setTestResult(null);
    try {
      const vars = {};
      Object.entries(testVars).forEach(([k, v]) => { vars[k] = Number(v) || 0; });
      const res = await evaluateFormula(calc.formula_expression, vars);
      setTestResult({ ok: true, value: res.data?.data?.result ?? res.data?.result });
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally {
      setTestRunning(false);
    }
  };

  // ── Validate ──────────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!basic.company_id)   e.company_id   = 'Required';
    if (!basic.bg_id)        e.bg_id        = 'Required';
    if (!basic.ldg_id)       e.ldg_id       = 'Required';
    // Element Name — required + PATTERNS.NAME (letters/spaces/hyphens only)
    if (!basic.element_name)
      e.element_name = 'Required';
    else if (!new RegExp(PATTERNS.NAME.pattern).test(basic.element_name))
      e.element_name = PATTERNS.NAME.patternMsg;
    if (!basic.type)         e.type         = 'Required';

    // Flat Amount — required when calculation_rule=flat (except BASIC element),
    // must be a finite non-negative number
    if (calc.calculation_rule === 'flat' && (basic.element_code !== 'BASIC')) {
      const raw = String(calc.flat_amount);
      if (raw === '') {
        e.flat_amount = 'Amount is required';
      } else {
        const n = Number(raw);
        if (!Number.isFinite(n)) e.flat_amount = 'Must be a valid number';
        else if (n < 0)          e.flat_amount = 'Must be 0 or greater';
      }
    }
    if (calc.calculation_rule === 'percentage') {
      const pct = Number(calc.percentage_value);
      if (!calc.percentage_value) e.percentage_value = 'Required';
      else if (pct < 0 || pct > 100) e.percentage_value = 'Must be 0–100';
    }
    if (calc.calculation_rule === 'formula') {
      // Formula Name — optional but if provided, must match PATTERNS.NAME
      if (calc.formula_name && !new RegExp(PATTERNS.NAME.pattern).test(calc.formula_name)) {
        e.formula_name = PATTERNS.NAME.patternMsg;
      }
      if (!calc.formula_expression.trim()) e.formula_expression = 'Expression is required';
      else if (undeclaredVars.length > 0)  e.formula_expression = `Undeclared variables: ${undeclaredVars.join(', ')}`;
    }
    const inputNames = inputs.map(i => i.input_value_name.trim());
    const dupeInputs = inputNames.filter((n, i) => n && inputNames.indexOf(n) !== i);
    if (dupeInputs.length) e.inputs = `Duplicate input names: ${dupeInputs.join(', ')}`;
    // Variable Name — must be a valid identifier (letters/digits/underscore,
    // must not start with digit)
    const badIdents = inputs
      .map((i, idx) => ({ idx, name: i.input_value_name.trim() }))
      .filter(x => x.name && !VAR_NAME_RE.test(x.name));
    if (badIdents.length) {
      e.inputs = (e.inputs ? e.inputs + ' • ' : '') +
        `Invalid variable name${badIdents.length > 1 ? 's' : ''}: ${badIdents.map(b => b.name).join(', ')} (use letters/digits/underscores, must start with a letter or underscore)`;
    }
    // Default Value — when provided, must be a single finite number within range.
    // Range depends on data_type: percentage → 0-100, number → 0-9,999,999.
    const badDefaults = inputs.map((inp, idx) => {
      const raw = String(inp.default_value ?? '').trim();
      if (raw === '') return null;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        return { idx, value: raw, reason: 'not a valid number' };
      }
      if (inp.data_type === 'percentage') {
        if (n < 0 || n > 100) return { idx, value: raw, reason: 'percentage must be 0-100' };
      } else {
        if (n < 0)         return { idx, value: raw, reason: 'must be 0 or greater' };
        if (n > 9999999)   return { idx, value: raw, reason: 'exceeds max 9,999,999' };
      }
      return null;
    }).filter(Boolean);
    if (badDefaults.length) {
      e.inputs = (e.inputs ? e.inputs + ' • ' : '') +
        `Invalid default value${badDefaults.length > 1 ? 's' : ''}: ${badDefaults.map(b => `"${b.value}" (${b.reason})`).join(', ')}`;
    }
    // Eligibility Value — must be a single finite numeric value (rules with non-FK fields)
    const badRules = rules
      .map((r, idx) => ({ idx, r }))
      .filter(x => x.r.condition_value !== '' && x.r.condition_value !== undefined
        && !['department', 'location'].includes(x.r.condition_field)
        && !Number.isFinite(Number(x.r.condition_value)));
    if (badRules.length) {
      e.rules = `Eligibility value must be a single numeric value (got: ${badRules.map(b => `"${b.r.condition_value}"`).join(', ')})`;
    }
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const tabErr = e.company_id || e.bg_id || e.ldg_id || e.element_name ? 0
        : e.flat_amount || e.percentage_value || e.formula_expression || e.formula_name ? 1
        : e.inputs ? 2
        : e.rules ? 3
        : 0;
      setTab(tabErr);
    }
    return Object.keys(e).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const elementPayload = {
        ...basic,
        ...settings,
        element_code:      basic.element_code || autoCode(basic.element_name),
        calculation_rule:  calc.calculation_rule,
        flat_amount:       calc.calculation_rule === 'flat'       ? (Number(calc.flat_amount) || 0) : null,
        percentage_value:  calc.calculation_rule === 'percentage' ? Number(calc.percentage_value)  : null,
        base_element:      calc.calculation_rule === 'percentage' ? calc.base_element              : null,
        formula_reference: null,
      };

      const formulaPayload = isFormula ? {
        formula_name:       calc.formula_name || (basic.element_name + ' Formula'),
        formula_code:       calc.formula_code || autoCode(basic.element_code || basic.element_name) + '_FRM',
        formula_expression: calc.formula_expression,
        formula_type:       basic.type === 'earning' ? 'earning' : 'deduction',
        company_id:         basic.company_id || null,
      } : null;

      const inputsPayload = isFormula
        ? inputs.filter(i => i.input_value_name.trim()).map(i => ({
            ...i,
            input_value_name: i.input_value_name.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          }))
        : [];

      const eligibilityPayload = rules
        .filter(r => r.condition_field && r.operator && r.condition_value.trim())
        .map(r => ({ ...r }));

      if (isEdit) {
        await updateElementType(record.element_id || record._id, {
          element: elementPayload, formula: formulaPayload,
          inputs: inputsPayload, eligibility: eligibilityPayload,
        });
        enqueueSnackbar('Element updated', { variant: 'success' });
      } else {
        await createElementType({
          element: elementPayload, formula: formulaPayload,
          inputs: inputsPayload, eligibility: eligibilityPayload,
        });
        enqueueSnackbar('Element created', { variant: 'success' });
      }
      onSaved?.();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered lookups (cascade: Business Group → Company) ─────────────────────
  // Filter Companies by the selected Business Group. The lookups endpoint exposes
  // the BG's parent company via `b.company_id`, so we resolve that to the single
  // associated Company. A tolerant fallback keeps the existing Company visible
  // even if it doesn't match (legacy data).
  const filteredCompanies = useMemo(() => {
    if (!basic.bg_id) return [];
    const selectedBg = lookups.businessGroups.find(b => b.id === basic.bg_id);
    let list = lookups.companies.filter(c => c.id === selectedBg?.company_id);
    if (basic.company_id && !list.find(c => c.id === basic.company_id)) {
      const legacy = lookups.companies.find(c => c.id === basic.company_id);
      if (legacy) list = [...list, legacy];
    }
    return list;
  }, [lookups.companies, lookups.businessGroups, basic.bg_id, basic.company_id]);

  // ── Tab panels ─────────────────────────────────────────────────────────────────

  const BasicPanel = (
    <Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<LayersIcon />} label="Organization Context" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Autocomplete size="small"
              options={lookups.businessGroups} getOptionLabel={o => o.label}
              value={lookups.businessGroups.find(o => o.id === basic.bg_id) || null}
              onChange={(_, v) => { setB('bg_id', v?.id || ''); setB('company_id', ''); }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => (
                <TextField {...p} label="Business Group *" error={!!errors.bg_id}
                  helperText={errors.bg_id} sx={inputSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete size="small"
              options={filteredCompanies} getOptionLabel={o => o.label}
              disabled={!basic.bg_id}
              value={filteredCompanies.find(o => o.id === basic.company_id) || null}
              onChange={(_, v) => setB('company_id', v?.id || '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText={!basic.bg_id ? 'Select a Business Group first' : 'No options'}
              renderInput={p => (
                <TextField {...p} label="Company *" error={!!errors.company_id}
                  helperText={errors.company_id || (!basic.bg_id ? 'Select a Business Group first' : '')}
                  sx={inputSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete size="small"
              options={lookups.legislations} getOptionLabel={o => o.label}
              value={lookups.legislations.find(o => o.id === basic.ldg_id) || null}
              onChange={(_, v) => setB('ldg_id', v?.id || '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => (
                <TextField {...p} label="Legislation *" error={!!errors.ldg_id}
                  helperText={errors.ldg_id} sx={inputSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small"
              options={lookups.payrolls} getOptionLabel={o => o.label}
              value={lookups.payrolls.find(o => o.id === basic.payroll_id) || null}
              onChange={(_, v) => setB('payroll_id', v?.id || '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => (
                <TextField {...p} label="Payroll (optional)" helperText="Leave blank to apply to all payrolls" sx={inputSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small"
              options={lookups.glAccounts || []} getOptionLabel={o => o.label || ''}
              value={(lookups.glAccounts || []).find(o => o.id === settings.gl_account_id) || null}
              onChange={(_, v) => setSettings(p => ({ ...p, gl_account_id: v?.id || '' }))}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => (
                <TextField {...p} label="GL Account" helperText="Account for general ledger posting" sx={inputSx} />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={cardSx}>
        <SectionHeader icon={<TuneIcon />} label="Element Identity" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <TextField size="small" fullWidth label="Element Name *"
              value={basic.element_name}
              onChange={e => {
                const v = e.target.value;
                setB('element_name', v);
                // Live auto-gen Element Code from name unless user has manually edited the code
                if (!elementCodeManual) setB('element_code', autoCode(v));
              }}
              error={!!errors.element_name}
              helperText={errors.element_name || 'Letters, spaces, hyphens, apostrophes and dots only'}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField size="small" fullWidth label="Element Code"
              value={basic.element_code}
              onChange={e => {
                setB('element_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'));
                setElementCodeManual(true);
              }}
              helperText={elementCodeManual ? 'Custom — edit name will not overwrite' : 'Auto-generated from Element Name'}
              sx={inputSx}
              inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={12.5} fontWeight={600} color="text.secondary" mb={1}>Element Type</Typography>
            <RadioGroup row value={basic.type} onChange={e => setB('type', e.target.value)}>
              {[
                { value: 'earning',   label: 'Earning',   color: '#16a34a', bg: '#f0fdf4' },
                { value: 'deduction', label: 'Deduction', color: '#dc2626', bg: '#fef2f2' },
              ].map(opt => (
                <Box key={opt.value}
                  onClick={() => setB('type', opt.value)}
                  sx={{
                    mr: 1.5, px: 2.5, py: 1.2, borderRadius: '10px', cursor: 'pointer',
                    border: `2px solid ${basic.type === opt.value ? opt.color : '#E2E8F0'}`,
                    bgcolor: basic.type === opt.value ? opt.bg : 'white',
                    transition: 'all 0.15s',
                  }}>
                  <Typography fontSize={13} fontWeight={700} color={basic.type === opt.value ? opt.color : '#64748B'}>
                    {opt.label}
                  </Typography>
                </Box>
              ))}
            </RadioGroup>
            {errors.type && <Typography fontSize={11} color="error" mt={0.5}>{errors.type}</Typography>}
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Switch size="small"
                checked={basic.active_flag === 'Y'}
                onChange={e => setB('active_flag', e.target.checked ? 'Y' : 'N')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
                }} />
              <Typography fontSize={13} fontWeight={600} color="#374151">Active</Typography>
              <Chip label={basic.active_flag === 'Y' ? 'Active' : 'Inactive'} size="small"
                sx={{
                  bgcolor: basic.active_flag === 'Y' ? '#f0fdf4' : '#fef2f2',
                  color:   basic.active_flag === 'Y' ? '#16a34a' : '#dc2626',
                  fontWeight: 700, fontSize: 11, height: 20,
                }} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const CalcPanel = (
    <Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<CalculateIcon />} label="Calculation Method" />
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          {[
            { value: 'flat',       label: 'Flat Amount',  desc: 'Fixed rupee value' },
            { value: 'percentage', label: 'Percentage',   desc: '% of another element' },
            { value: 'formula',    label: 'Formula',      desc: 'Custom expression' },
          ].map(opt => (
            <Box key={opt.value}
              onClick={() => setC('calculation_rule', opt.value)}
              sx={{
                flex: '1 0 140px', p: 2, borderRadius: '12px', cursor: 'pointer',
                border: `2px solid ${calc.calculation_rule === opt.value ? TEAL : '#E2E8F0'}`,
                bgcolor: calc.calculation_rule === opt.value ? TEAL_L : 'white',
                transition: 'all 0.15s',
              }}>
              <Typography fontSize={13} fontWeight={700}
                color={calc.calculation_rule === opt.value ? TEAL_D : '#374151'}>{opt.label}</Typography>
              <Typography fontSize={11} color="text.secondary">{opt.desc}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Flat */}
      {calc.calculation_rule === 'flat' && (
        <Box sx={cardSx}>
          <SectionHeader icon={<TuneIcon />} label="Flat Amount" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField size="small" fullWidth label="Amount (₹) *"
                type="number"
                value={calc.flat_amount}
                onChange={e => setC('flat_amount', e.target.value)}
                error={!!errors.flat_amount} helperText={errors.flat_amount || 'Enter a fixed monetary value'}
                sx={inputSx}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Percentage */}
      {calc.calculation_rule === 'percentage' && (
        <Box sx={cardSx}>
          <SectionHeader icon={<TuneIcon />} label="Percentage Configuration" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField size="small" fullWidth label="Percentage (%) *"
                type="number"
                value={calc.percentage_value}
                onChange={e => setC('percentage_value', e.target.value)}
                error={!!errors.percentage_value} helperText={errors.percentage_value || '0–100'}
                sx={inputSx}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Autocomplete size="small"
                options={[
                  ...lookups.elements.map(e => ({ value: e.code, label: e.label })),
                ]}
                getOptionLabel={o => o.label}
                value={{ value: calc.base_element, label: calc.base_element }}
                onChange={(_, v) => setC('base_element', v?.value || 'BASIC')}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={p => (
                  <TextField {...p} label="Based On" helperText="Element this % is calculated from" sx={inputSx} />
                )}
              />
            </Grid>
          </Grid>
          {calc.percentage_value && calc.base_element && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: TEAL_L, border: `1px solid ${alpha(TEAL, 0.2)}` }}>
              <Typography fontSize={12} color={TEAL_D} fontWeight={600}>
                = {calc.percentage_value}% × {calc.base_element}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Formula */}
      {calc.calculation_rule === 'formula' && (
        <Box sx={cardSx}>
          <SectionHeader icon={<TuneIcon />} label="Formula Definition" sub="Use variable names matching Input tab entries" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField size="small" fullWidth label="Formula Name"
                value={calc.formula_name}
                onChange={e => {
                  const v = e.target.value;
                  setC('formula_name', v);
                  // Live auto-gen Formula Code from name unless user has manually edited the code
                  if (!formulaCodeManual) setC('formula_code', autoCode(v));
                }}
                error={!!errors.formula_name}
                helperText={errors.formula_name || 'Letters, spaces, hyphens, apostrophes and dots only'}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField size="small" fullWidth label="Formula Code"
                value={calc.formula_code}
                onChange={e => {
                  setC('formula_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'));
                  setFormulaCodeManual(true);
                }}
                helperText={formulaCodeManual ? 'Custom — edit name will not overwrite' : 'Auto-generated from Formula Name'}
                sx={inputSx}
                inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3}
                label="Formula Expression *"
                value={calc.formula_expression}
                onChange={e => setC('formula_expression', e.target.value)}
                error={!!errors.formula_expression}
                helperText={errors.formula_expression || 'Example: basic * rate / 100'}
                sx={{ ...inputSx, '& textarea': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7 } }}
              />
            </Grid>
          </Grid>

          {/* Detected variables */}
          {formulaVars.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                <Typography fontSize={11} color="text.secondary" mr={0.5}>Detected variables:</Typography>
                {formulaVars.map(v => {
                  const isDeclared = declaredNames.includes(v);
                  return (
                    <Tooltip key={v} title={isDeclared ? 'Declared in Inputs' : 'Click to add to Inputs'}>
                      <Chip
                        label={v}
                        size="small"
                        onClick={() => !isDeclared && autoFillInput(v)}
                        icon={isDeclared ? <CheckCircleIcon sx={{ fontSize: '12px !important', color: '#16a34a !important' }} /> : <WarningAmberIcon sx={{ fontSize: '12px !important' }} />}
                        sx={{
                          fontFamily: 'monospace', fontSize: 11, height: 22, cursor: isDeclared ? 'default' : 'pointer',
                          bgcolor: isDeclared ? '#f0fdf4' : '#fff7ed',
                          color:   isDeclared ? '#16a34a' : '#d97706',
                          fontWeight: 700,
                          border: `1px solid ${isDeclared ? '#bbf7d0' : '#fed7aa'}`,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
              {undeclaredVars.length > 0 && (
                <Typography fontSize={11} color="#d97706" mt={0.75}>
                  ↑ Click undeclared variables to add them as Inputs, or switch to the Inputs tab.
                </Typography>
              )}
            </Box>
          )}

          {/* Test panel */}
          {formulaVars.length > 0 && (
            <Box sx={{ mt: 2.5, p: 2, bgcolor: '#F8FAFC', borderRadius: '10px', border: '1.5px solid #E8EDF5' }}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mb={1.5} letterSpacing={0.5} textTransform="uppercase">
                Test Formula
              </Typography>
              <Grid container spacing={1.5}>
                {formulaVars.map(v => (
                  <Grid item xs={6} sm={3} key={v}>
                    <TextField size="small" fullWidth label={v} type="number"
                      value={testVars[v] ?? ''}
                      onChange={e => setTestVars(p => ({ ...p, [v]: e.target.value }))}
                      sx={{ ...inputSx, '& input': { fontFamily: 'monospace' } }}
                      inputProps={{ step: 'any' }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Stack direction="row" alignItems="center" spacing={2} mt={1.5}>
                <Button size="small" variant="outlined"
                  startIcon={testRunning ? <CircularProgress size={14} sx={{ color: TEAL }} /> : <PlayArrowIcon />}
                  onClick={handleRunTest} disabled={testRunning}
                  sx={{ borderRadius: '8px', fontWeight: 600, borderColor: TEAL, color: TEAL,
                    '&:hover': { borderColor: TEAL_D, bgcolor: alpha(TEAL, 0.06) } }}>
                  {testRunning ? 'Running…' : 'Run Test'}
                </Button>
                {testResult && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: '8px',
                    bgcolor: testResult.ok ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}`,
                  }}>
                    {testResult.ok
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                      : <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                    <Typography fontSize={13} fontWeight={700} fontFamily="monospace"
                      color={testResult.ok ? '#16a34a' : '#dc2626'}>
                      {testResult.ok ? `= ${testResult.value}` : testResult.message}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const InputsPanel = (
    <Box>
      {!isFormula ? (
        <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
          <InfoOutlinedIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
          <Typography fontSize={14} fontWeight={600} color="text.secondary">
            Inputs are only used with Formula calculation
          </Typography>
          <Typography fontSize={12} color="text.secondary" mt={0.5}>
            Switch to Formula in the Calculation tab to define variables.
          </Typography>
          <Button variant="outlined" size="small" sx={{ mt: 2, borderRadius: '8px', borderColor: TEAL, color: TEAL }}
            onClick={() => { setC('calculation_rule', 'formula'); setTab(1); }}>
            Switch to Formula
          </Button>
        </Box>
      ) : (
        <Box sx={cardSx}>
          <SectionHeader icon={<InputIcon />} label="Formula Input Variables"
            sub="Variables referenced in your formula expression" />

          {errors.inputs && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
              <Typography fontSize={12} color="#dc2626" fontWeight={600}>{errors.inputs}</Typography>
            </Box>
          )}

          {undeclaredVars.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarningAmberIcon sx={{ fontSize: 16, color: '#d97706' }} />
                <Typography fontSize={12} color="#d97706" fontWeight={600}>
                  Formula uses undeclared variables: {undeclaredVars.join(', ')}
                </Typography>
              </Stack>
              <Button size="small" sx={{ mt: 1, fontSize: 11, color: '#d97706', p: 0 }}
                onClick={() => undeclaredVars.forEach(v => autoFillInput(v))}>
                Add all missing inputs →
              </Button>
            </Box>
          )}

          {inputs.length > 0 ? (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {['Variable Name', 'Data Type', 'Default Value', 'Required', ''].map(h => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: 0.5, py: 1, border: 'none' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {inputs.map((inp, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                    <TableCell sx={{ py: 0.5, border: 'none' }}>
                      <TextField size="small" value={inp.input_value_name}
                        onChange={e => setInput(i, 'input_value_name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                        placeholder="variable_name"
                        error={!!inp.input_value_name && !VAR_NAME_RE.test(inp.input_value_name)}
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                        sx={{ ...inputSx, width: 160 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, border: 'none' }}>
                      <Select size="small" value={inp.data_type}
                        onChange={e => setInput(i, 'data_type', e.target.value)}
                        sx={{ borderRadius: '8px', fontSize: 13, minWidth: 120 }}>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, border: 'none' }}>
                      <TextField size="small" value={inp.default_value}
                        onChange={e => setInput(i, 'default_value', e.target.value)}
                        placeholder="optional"
                        type="number"
                        error={(() => {
                          const raw = String(inp.default_value ?? '').trim();
                          if (raw === '') return false;
                          const n = Number(raw);
                          if (!Number.isFinite(n)) return true;
                          if (inp.data_type === 'percentage') return n < 0 || n > 100;
                          return n < 0 || n > 9999999;
                        })()}
                        inputProps={{
                          style: { fontFamily: 'monospace' },
                          min: 0,
                          max: inp.data_type === 'percentage' ? 100 : 9999999,
                        }}
                        sx={{ ...inputSx, width: 110 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, border: 'none' }} align="center">
                      <Switch size="small" checked={!!inp.required_flag}
                        onChange={e => setInput(i, 'required_flag', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
                        }} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, border: 'none' }}>
                      <IconButton size="small" onClick={() => removeInput(i)}
                        sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography fontSize={13} color="text.secondary">No inputs defined yet.</Typography>
            </Box>
          )}

          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addInput}
            sx={{ borderRadius: '8px', fontWeight: 600, borderColor: TEAL, color: TEAL,
              '&:hover': { borderColor: TEAL_D, bgcolor: alpha(TEAL, 0.06) } }}>
            Add Input
          </Button>
        </Box>
      )}
    </Box>
  );

  const EligibilityPanel = (
    <Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<VerifiedIcon />} label="Eligibility Rules"
          sub="Define conditions that employees must meet. Leave empty to apply to all." />

        {errors.rules && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography fontSize={12} color="#dc2626" fontWeight={600}>{errors.rules}</Typography>
          </Box>
        )}

        {rules.length === 0 ? (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
              <Typography fontSize={12} color="#16a34a" fontWeight={600}>
                No rules — this element applies to all employees.
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            <Typography fontSize={12} color="text.secondary" mb={1.5} fontStyle="italic">
              Apply this element if: (all conditions must be true)
            </Typography>
            <Stack spacing={1} mb={2}>
              {rules.map((rule, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.5, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1.5px solid #E8EDF5',
                }}>
                  {i > 0 && (
                    <Chip label="AND" size="small"
                      sx={{ bgcolor: alpha(TEAL, 0.1), color: TEAL_D, fontWeight: 700, fontSize: 10, height: 18, mr: -0.5 }} />
                  )}
                  <FormControl size="small" sx={{ minWidth: 160, ...inputSx }}>
                    <InputLabel>Field</InputLabel>
                    <Select value={rule.condition_field} label="Field"
                      onChange={e => setRule(i, 'condition_field', e.target.value)}>
                      {[
                        ...lookups.elements.map(e => ({ value: e.code, label: e.label })),
                      ].map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 80, ...inputSx }}>
                    <InputLabel>Op</InputLabel>
                    <Select value={rule.operator} label="Op"
                      onChange={e => setRule(i, 'operator', e.target.value)}>
                      {OPERATORS.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {['department', 'location'].includes(rule.condition_field) ? (
                    <Autocomplete size="small"
                      sx={{ minWidth: 180, ...inputSx }}
                      options={rule.condition_field === 'department' ? lookups.departments : lookups.locations}
                      getOptionLabel={o => o.label}
                      value={
                        (rule.condition_field === 'department' ? lookups.departments : lookups.locations)
                          .find(o => o.id === rule.condition_value) || null
                      }
                      onChange={(_, v) => setRule(i, 'condition_value', v?.id || '')}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={p => <TextField {...p} label="Value" sx={inputSx} />}
                    />
                  ) : (
                    <TextField size="small" label="Value" type="number"
                      value={rule.condition_value}
                      onChange={e => setRule(i, 'condition_value', e.target.value)}
                      sx={{ ...inputSx, minWidth: 140 }}
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                    />
                  )}
                  <IconButton size="small" onClick={() => removeRule(i)}
                    sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' }, ml: 'auto' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </>
        )}

        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRule}
          sx={{ borderRadius: '8px', fontWeight: 600, borderColor: TEAL, color: TEAL,
            '&:hover': { borderColor: TEAL_D, bgcolor: alpha(TEAL, 0.06) } }}>
          Add Rule
        </Button>
      </Box>
    </Box>
  );

  const SettingsPanel = (
    <Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<TuneIcon />} label="Processing" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField size="small" fullWidth label="Processing Priority"
              type="number"
              value={settings.processing_priority}
              onChange={e => setSettings(p => ({ ...p, processing_priority: Number(e.target.value) }))}
              helperText="Lower = processed earlier (1 = first)"
              sx={inputSx}
              inputProps={{ min: 1, max: 999 }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
          </Grid>
        </Grid>
      </Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<TuneIcon />} label="Statutory & Tax Flags" />
        <FlagRow label="Taxable" hint="Include this earning in taxable income"
          checked={settings.taxable_flag} onChange={v => setSettings(p => ({ ...p, taxable_flag: v }))} />
        <FlagRow label="PF Applicable" hint="Count this element for Provident Fund calculation"
          checked={settings.pf_applicable} onChange={v => setSettings(p => ({ ...p, pf_applicable: v }))} />
        <FlagRow label="ESI Applicable" hint="Include in Employee State Insurance wage base"
          checked={settings.esi_applicable} onChange={v => setSettings(p => ({ ...p, esi_applicable: v }))} />
        <FlagRow label="Show in Payslip" hint="Display this component on the employee payslip"
          checked={settings.show_in_payslip} onChange={v => setSettings(p => ({ ...p, show_in_payslip: v }))} />
        <FlagRow label="Pro-rata Calculation" hint="Reduce proportionally for partial-month attendance"
          checked={settings.pro_rata_flag} onChange={v => setSettings(p => ({ ...p, pro_rata_flag: v }))} />
      </Box>
    </Box>
  );

  const panels = [BasicPanel, CalcPanel, InputsPanel, EligibilityPanel, SettingsPanel];

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', height: '90vh', display: 'flex', flexDirection: 'column' } }}>

      {/* Header */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${TEAL_D} 0%, ${TEAL} 100%)`,
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LayersIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize={16} color="white" lineHeight={1.2}>
                {isEdit
                  ? `Edit: ${record?.element_name || 'Element Setup'}`
                  : 'New Salary Structure (Component)'}
              </Typography>
              <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                {basic.element_code ? `Code: ${basic.element_code} · ` : ''}
                Configure element across 5 tabs
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ bgcolor: 'white', borderBottom: '1.5px solid #E8EDF5' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            variant="scrollable" scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, fontSize: 12.5, fontWeight: 600, textTransform: 'none', minWidth: 100 },
              '& .Mui-selected': { color: TEAL },
              '& .MuiTabs-indicator': { bgcolor: TEAL, height: 2.5, borderRadius: '2px 2px 0 0' },
            }}>
            {TABS.map((t, i) => (
              <Tab key={t.key}
                disabled={t.key === 'inputs' && !isFormula}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    {React.cloneElement(t.icon, { sx: { fontSize: 15 } })}
                    <span>{t.label}</span>
                    {t.key === 'inputs' && isFormula && inputs.length > 0 && (
                      <Chip label={inputs.length} size="small"
                        sx={{ height: 16, fontSize: 10, bgcolor: alpha(TEAL, 0.12), color: TEAL, fontWeight: 700,
                          '& .MuiChip-label': { px: 0.75 } }} />
                    )}
                    {t.key === 'eligibility' && rules.length > 0 && (
                      <Chip label={rules.length} size="small"
                        sx={{ height: 16, fontSize: 10, bgcolor: '#fff7ed', color: '#d97706', fontWeight: 700,
                          '& .MuiChip-label': { px: 0.75 } }} />
                    )}
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', bgcolor: '#F8FAFC' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={36} sx={{ color: TEAL }} />
          </Box>
        ) : (
          <Box sx={{ height: '100%', overflowY: 'auto', p: 3 }}>
            {panels[tab]}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1.5px solid #E8EDF5', gap: 1, flexShrink: 0 }}>
        {/* Tab navigation pills */}
        <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
          <Button size="small" disabled={tab === 0} onClick={() => setTab(t => t - 1)}
            sx={{ borderRadius: '8px', fontSize: 12, color: '#64748B' }}>
            ← Prev
          </Button>
          <Button size="small" disabled={tab === TABS.length - 1} onClick={() => setTab(t => t + 1)}
            sx={{ borderRadius: '8px', fontSize: 12, color: TEAL }}>
            Next →
          </Button>
        </Stack>
        <Button onClick={onClose} variant="outlined"
          sx={{ borderRadius: '10px', fontWeight: 600, fontSize: 13,
            borderColor: '#E2E8F0', color: '#64748B',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' } }}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
          variant="contained"
          sx={{
            borderRadius: '10px', fontWeight: 700, fontSize: 13,
            background: `linear-gradient(135deg, ${TEAL_D}, ${TEAL})`,
            boxShadow: `0 4px 14px ${alpha(TEAL, 0.4)}`,
          }}>
          {saving ? 'Saving…' : (isEdit ? 'Update Element' : 'Create Element')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
